// -----------------------------------------------------------------------------
// 🧩 File: Hero1.jsx (show membership + remaining fillups)
// -----------------------------------------------------------------------------
import React, { useEffect, useMemo, useState } from 'react';
import Classes from '../Dashboard.module.css';
import { CalenderIcon, CarFrontIcon, CrownIcon, GasPumpIcon } from '../../../../../../utils/Icons';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import backend from '../../../../../../api';

const fmt = (d) => (d ? new Date(d).toLocaleDateString() : '—');
const labelFor = (key) => (!key || key === 'free' ? 'None' : key?.charAt(0).toUpperCase() + key?.slice(1));

export default function Hero1() {
  const navigateTo = useNavigate();
  const { user, token } = useSelector((s) => s?.auth || {});
  const userId = user?._id;
  const vehicles = user?.customerProfile?.vehicle?.vehicles || [];

  const [snap, setSnap] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);

  // load sub snapshot + bookings in current period
  useEffect(() => {
    async function loadAll() {
      if (!userId) return;
      setLoading(true);
      try {
        const [{ data: subs }, { data: bookingRes }] = await Promise.all([
          backend.get(`/api/stripe/subscriptions/me/${userId}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          }),
          backend.get(`/api/bookings/customer?userId=${userId}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          }),
        ]);
        setSnap(subs || {});
        setBookings(bookingRes?.data || []);
      } catch (e) {
        console.error('hero load error', e);
        setSnap(null);
        setBookings([]);
      } finally {
        setLoading(false);
      }
    }
    loadAll();
  }, [userId, token]);

  const active = snap?.activeSubscription || {};
  const rawStatus = (active?.status || 'none').toLowerCase();
  const planKeyRaw = active?.plan?.key || snap?.legacyLabel || 'free';
  const limit = Number(active?.plan?.fillupsLimit || 0); // 10 for basic, 0 for unlimited
  const periodStart = active?.current_period_start ? new Date(active.current_period_start) : null;
  const periodEnd = active?.current_period_end ? new Date(active.current_period_end) : null;

  // determine if sub is active (not canceled and not ended)
  const nowTs = Date.now();
  const endTs = periodEnd ? periodEnd.getTime() : null;
  const isEnded = !!endTs && endTs < nowTs;
  const isCancelled = rawStatus === 'canceled' || rawStatus === 'cancelled';
  const hasActiveSub = rawStatus !== 'none' && !isCancelled && !isEnded;

  // label to show
  const planLabel = hasActiveSub ? labelFor(planKeyRaw) : 'None';

  // count bookings within current period (treat any status except canceled as a fill-up)
  const usedThisPeriod = useMemo(() => {
    if (!Array.isArray(bookings) || !hasActiveSub || !periodStart || !periodEnd) return 0;
    const ps = periodStart.getTime();
    const pe = periodEnd.getTime();
    return bookings.filter(b => {
      const d = b?.appointment?.date ? new Date(b.appointment.date).getTime() : null;
      if (!d) return false;
      if (d < ps || d > pe) return false;
      const st = (b?.status || '').toLowerCase();
      if (st === 'canceled' || st === 'cancelled') return false;
      return true;
    }).length;
  }, [bookings, hasActiveSub, periodStart, periodEnd]);

  const remaining = hasActiveSub
    ? (limit > 0 ? Math.max(0, limit - usedThisPeriod) : 'Unlimited')
    : '—';

  return (
    <>
      <div className={Classes.hero1}>
        <div className={Classes.heroBox}>
          <div className={Classes.hero1Img}>
            <CrownIcon height='27px' width='27px' fill='white' />
          </div>
          <div>
            <h5 className={Classes.title1}>Membership</h5>
            <h3 className='pointer' onClick={() => { navigateTo('/settings/membership') }}>
              {loading ? 'Loading...' : planLabel} <span> {'>'} </span>
            </h3>
            {/* {hasActiveSub && periodStart && periodEnd && (
              <div className={Classes.subText}>
                Period: {fmt(periodStart)} – {fmt(periodEnd)}
              </div>
            )} */}
          </div>
        </div>

        <div className={Classes.miniLine} />

        <div className={Classes.heroBox}>
          <div className={Classes.hero1Img}>
            <GasPumpIcon height='27px' width='27px' fill='white' />
          </div>
          <div>
            <h5 className={Classes.title1}>Fill Ups Remaining</h5>
            <h3 className='pointer' onClick={() => { navigateTo('/settings/membership') }}>
              {loading ? '—' : remaining} <span> {'>'} </span>
            </h3>
          </div>
        </div>

        <div className={Classes.miniLine} />

        <div className={Classes.heroBox}>
          <div className={Classes.hero1Img}>
            <CarFrontIcon height='27px' width='27px' fill='white' />
          </div>
          <div>
            <h5 className={Classes.title1}>Vehicles</h5>
            <h3 className='pointer' onClick={() => { navigateTo('/settings/vehicles') }}>
              {vehicles?.length}<span> {'>'} </span>
            </h3>
          </div>
        </div>

        <div className={Classes.miniLine} />

        <div className={Classes.heroBox}>
          <div className={Classes.hero1Img}>
            <CalenderIcon height='27px' width='27px' fill='white' />
          </div>
          <div>
            <h5 className={Classes.title1}>Appointments</h5>
            <div className={Classes.flexAlign5}>
              <h3 className='pointer' onClick={() => { navigateTo('/appointments') }}>
                {bookings?.length || 0} <span> {'>'} </span>
              </h3>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
